/**
 * @file   src\components\Introduction\CustomizeIntroModal.js
 * @brief  This file is responsible for the view of customize introduction.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Row, Col, Button } from "react-bootstrap";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import DragIcon from "../../images/drag-icon.svg";
import IntroductionPreviewModal from "../Introduction/v2/IntroductionPreviewModal";
import noImage from "../../images/no-image-placeholder.png";
import inValidImage from "../../images/invalid-image-placeholder.png";

const CustomizeIntroModal = (props) => {
  const { show, selectedProperty, onEditClick, reload } = props;

  // Initialize component state variables and constants.
  const inventoryPhotos = selectedProperty?.photosForPreview;
  const [coverPhotos, setCoverPhotos] = useState([]);
  const [disableEditButton, setDisableEditButton] = useState(false);
  const propertyFeatures = selectedProperty?.propertyFeaturesForPreview;
  const [featuredProperty, setFeaturedProperty] = useState([]);

  const [propertyFeatureReorder, setPropertyFeatureReorder] = useState(false);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const PreviewModalClose = () => setShowPreviewModal(false);
  const PreviewModalShow = () => setShowPreviewModal(true);
  const [dragId, setDragId] = useState();

  //inventoryPhotos and reload  state change side effect handling.
  useEffect(() => {
    if (inventoryPhotos && inventoryPhotos.length > 0) {
      const defaultArray = inventoryPhotos?.filter((item) => item.isCover == 1);
      setCoverPhotos(defaultArray);
      if (inventoryPhotos.length <= 3) {
        setDisableEditButton(true);
      }
    } else {
      setCoverPhotos([]);
    }
  }, [inventoryPhotos, reload]);

  //propertyFeatures and reload  state change side effect handling.
  useEffect(() => {
    if (propertyFeatures && propertyFeatures.length > 0) {
      setFeaturedProperty(propertyFeatures);
    } else {
      setFeaturedProperty([]);
    }
    setPropertyFeatureReorder(false);
  }, [propertyFeatures, reload]);

  // Handle the drag of featured property values
  const handleDrag = (ev) => {
    setDragId(ev.currentTarget.id);
  };

  // Handle the drop of featured property values
  const handleDrop = (ev) => {
    let currentItem = featuredProperty[dragId];
    let currentSelectedItemRemovedArray = [
      ...featuredProperty.slice(0, Number(dragId)),
      ...featuredProperty.slice(Number(dragId) + 1),
    ];

    let NewArray = [
      ...currentSelectedItemRemovedArray.slice(0, Number(ev.currentTarget.id)),
      currentItem,
      ...currentSelectedItemRemovedArray.slice(Number(ev.currentTarget.id)),
    ];

    let newPropertyFeatures = NewArray.map((feature, index) => {
      return { ...feature, order: index + 1 };
    });

    let orderdPropertyFeature = newPropertyFeatures.sort(
      (a, b) => a.order - b.order
    );
    setFeaturedProperty(orderdPropertyFeature);
  };

  // On final save of customize intro values
  const SaveChanges = () => {
    props.onHide();
    props.onSavePropertyFeatureOrder(featuredProperty);
  };

  // To save the reoreder propery features values
  const saveReordedPropertyFeature = () => {
    props.savePropertyFeaturesforPreview(featuredProperty);
    setPropertyFeatureReorder(false);
  };

  // Tooltip rendering
  const renderTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      Pick up to 3 photos to show buyers in your introduction. Once accepted,
      they can view all your property photos.
    </Tooltip>
  );

  // Property features tooltip
  const renderTooltipForPropertyFeature = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      Change the order of your property features to highlight what's most
      important for this want listing.
    </Tooltip>
  );

  return (
    <>
      <Modal
        className="customize-intro-modal"
        show={show}
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header className="position-relative">
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={props.onClickCustizeIntroClose}
          ></button>
          Customize intro
        </Modal.Header>
        <Modal.Body className="pb-24 pt-32">
          <div className="customize-intro-header mb-3">
            <h5>
              Cover photos
              <span>
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={renderTooltip}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.75 11.25C8.75 11.3983 8.70602 11.5433 8.6236 11.6667C8.54119 11.79 8.42406 11.8861 8.28701 11.9429C8.14997 11.9997 7.99917 12.0145 7.85368 11.9856C7.7082 11.9566 7.57456 11.8852 7.46967 11.7803C7.36478 11.6754 7.29335 11.5418 7.26441 11.3963C7.23548 11.2508 7.25033 11.1 7.30709 10.963C7.36386 10.8259 7.45999 10.7088 7.58333 10.6264C7.70666 10.544 7.85167 10.5 8 10.5C8.19892 10.5 8.38968 10.579 8.53033 10.7197C8.67098 10.8603 8.75 11.0511 8.75 11.25ZM8 4.5C6.62125 4.5 5.5 5.50938 5.5 6.75V7C5.5 7.13261 5.55268 7.25979 5.64645 7.35355C5.74022 7.44732 5.86739 7.5 6 7.5C6.13261 7.5 6.25979 7.44732 6.35356 7.35355C6.44732 7.25979 6.5 7.13261 6.5 7V6.75C6.5 6.0625 7.17313 5.5 8 5.5C8.82688 5.5 9.5 6.0625 9.5 6.75C9.5 7.4375 8.82688 8 8 8C7.86739 8 7.74022 8.05268 7.64645 8.14645C7.55268 8.24021 7.5 8.36739 7.5 8.5V9C7.5 9.13261 7.55268 9.25979 7.64645 9.35355C7.74022 9.44732 7.86739 9.5 8 9.5C8.13261 9.5 8.25979 9.44732 8.35356 9.35355C8.44732 9.25979 8.5 9.13261 8.5 9V8.955C9.64 8.74563 10.5 7.83625 10.5 6.75C10.5 5.50938 9.37875 4.5 8 4.5ZM14.5 8C14.5 9.28558 14.1188 10.5423 13.4046 11.6112C12.6903 12.6801 11.6752 13.5132 10.4874 14.0052C9.29973 14.4972 7.99279 14.6259 6.73192 14.3751C5.47104 14.1243 4.31285 13.5052 3.40381 12.5962C2.49477 11.6872 1.8757 10.529 1.6249 9.26809C1.37409 8.00721 1.50282 6.70028 1.99479 5.51256C2.48676 4.32484 3.31988 3.30968 4.3888 2.59545C5.45772 1.88122 6.71442 1.5 8 1.5C9.72335 1.50182 11.3756 2.18722 12.5942 3.40582C13.8128 4.62441 14.4982 6.27665 14.5 8ZM13.5 8C13.5 6.9122 13.1774 5.84883 12.5731 4.94436C11.9687 4.03989 11.1098 3.33494 10.1048 2.91866C9.09977 2.50238 7.9939 2.39346 6.92701 2.60568C5.86011 2.8179 4.8801 3.34172 4.11092 4.11091C3.34173 4.8801 2.8179 5.86011 2.60568 6.927C2.39347 7.9939 2.50238 9.09977 2.91867 10.1048C3.33495 11.1098 4.0399 11.9687 4.94437 12.5731C5.84884 13.1774 6.91221 13.5 8 13.5C9.45819 13.4983 10.8562 12.9184 11.8873 11.8873C12.9184 10.8562 13.4983 9.45818 13.5 8Z"
                      fill="black"
                    />
                  </svg>
                </OverlayTrigger>
              </span>
            </h5>

            <a
              href={void 0}
              className="intro-right-link me-2 ms-2"
              onClick={() => {
                onEditClick();
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11.6464 1.64645C11.8417 1.45118 12.1583 1.45118 12.3536 1.64645L14.3536 3.64645C14.5488 3.84171 14.5488 4.15829 14.3536 4.35355L8.35355 10.3536C8.25979 10.4473 8.13261 10.5 8 10.5H6C5.72386 10.5 5.5 10.2761 5.5 10V8C5.5 7.86739 5.55268 7.74021 5.64645 7.64645L11.6464 1.64645ZM6.5 8.20711V9.5H7.79289L13.2929 4L12 2.70711L6.5 8.20711Z"
                  fill="#022964"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.1464 3.14645C10.3417 2.95118 10.6583 2.95118 10.8536 3.14645L12.8536 5.14645C13.0488 5.34171 13.0488 5.65829 12.8536 5.85355C12.6583 6.04882 12.3417 6.04882 12.1464 5.85355L10.1464 3.85355C9.95118 3.65829 9.95118 3.34171 10.1464 3.14645Z"
                  fill="#022964"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2.29289 2.29289C2.48043 2.10536 2.73478 2 3 2H8.5C8.77614 2 9 2.22386 9 2.5C9 2.77614 8.77614 3 8.5 3L3 3L3 13H13V7.5C13 7.22386 13.2239 7 13.5 7C13.7761 7 14 7.22386 14 7.5V13C14 13.2652 13.8946 13.5196 13.7071 13.7071C13.5196 13.8946 13.2652 14 13 14H3C2.73478 14 2.48043 13.8946 2.29289 13.7071C2.10536 13.5196 2 13.2652 2 13V3C2 2.73478 2.10536 2.48043 2.29289 2.29289Z"
                  fill="#022964"
                />
              </svg>
              Edit
            </a>
          </div>
          <div className="intro-img-scroll mb-32">
            <Row className="g-2 g-sm-2 intro-img-wrap">
              {coverPhotos.length == 0 ? (
                <Col sm={6} md={4} className="">
                  <div className="intro-img-height">
                    <img src={noImage} />
                  </div>
                </Col>
              ) : (
                coverPhotos &&
                coverPhotos
                  .sort((a, b) => a.order - b.order)
                  .map((photo) => {
                    return (
                      <Col sm={6} md={4} className="">
                        <div className="intro-img-height">
                          <img
                            src={
                              photo?.IsValid != 0 ? photo?.image : inValidImage
                            }
                          />
                        </div>
                      </Col>
                    );
                  })
              )}
            </Row>
          </div>

          {featuredProperty.length > 0 && (
            <>
              <div className="customize-intro-header mb-3">
                <h5>
                  Property features{" "}
                  <span>
                    <OverlayTrigger
                      placement="bottom"
                      delay={{ show: 250, hide: 400 }}
                      overlay={renderTooltipForPropertyFeature}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.75 11.25C8.75 11.3983 8.70602 11.5433 8.6236 11.6667C8.54119 11.79 8.42406 11.8861 8.28701 11.9429C8.14997 11.9997 7.99917 12.0145 7.85368 11.9856C7.7082 11.9566 7.57456 11.8852 7.46967 11.7803C7.36478 11.6754 7.29335 11.5418 7.26441 11.3963C7.23548 11.2508 7.25033 11.1 7.30709 10.963C7.36386 10.8259 7.45999 10.7088 7.58333 10.6264C7.70666 10.544 7.85167 10.5 8 10.5C8.19892 10.5 8.38968 10.579 8.53033 10.7197C8.67098 10.8603 8.75 11.0511 8.75 11.25ZM8 4.5C6.62125 4.5 5.5 5.50938 5.5 6.75V7C5.5 7.13261 5.55268 7.25979 5.64645 7.35355C5.74022 7.44732 5.86739 7.5 6 7.5C6.13261 7.5 6.25979 7.44732 6.35356 7.35355C6.44732 7.25979 6.5 7.13261 6.5 7V6.75C6.5 6.0625 7.17313 5.5 8 5.5C8.82688 5.5 9.5 6.0625 9.5 6.75C9.5 7.4375 8.82688 8 8 8C7.86739 8 7.74022 8.05268 7.64645 8.14645C7.55268 8.24021 7.5 8.36739 7.5 8.5V9C7.5 9.13261 7.55268 9.25979 7.64645 9.35355C7.74022 9.44732 7.86739 9.5 8 9.5C8.13261 9.5 8.25979 9.44732 8.35356 9.35355C8.44732 9.25979 8.5 9.13261 8.5 9V8.955C9.64 8.74563 10.5 7.83625 10.5 6.75C10.5 5.50938 9.37875 4.5 8 4.5ZM14.5 8C14.5 9.28558 14.1188 10.5423 13.4046 11.6112C12.6903 12.6801 11.6752 13.5132 10.4874 14.0052C9.29973 14.4972 7.99279 14.6259 6.73192 14.3751C5.47104 14.1243 4.31285 13.5052 3.40381 12.5962C2.49477 11.6872 1.8757 10.529 1.6249 9.26809C1.37409 8.00721 1.50282 6.70028 1.99479 5.51256C2.48676 4.32484 3.31988 3.30968 4.3888 2.59545C5.45772 1.88122 6.71442 1.5 8 1.5C9.72335 1.50182 11.3756 2.18722 12.5942 3.40582C13.8128 4.62441 14.4982 6.27665 14.5 8ZM13.5 8C13.5 6.9122 13.1774 5.84883 12.5731 4.94436C11.9687 4.03989 11.1098 3.33494 10.1048 2.91866C9.09977 2.50238 7.9939 2.39346 6.92701 2.60568C5.86011 2.8179 4.8801 3.34172 4.11092 4.11091C3.34173 4.8801 2.8179 5.86011 2.60568 6.927C2.39347 7.9939 2.50238 9.09977 2.91867 10.1048C3.33495 11.1098 4.0399 11.9687 4.94437 12.5731C5.84884 13.1774 6.91221 13.5 8 13.5C9.45819 13.4983 10.8562 12.9184 11.8873 11.8873C12.9184 10.8562 13.4983 9.45818 13.5 8Z"
                          fill="black"
                        />
                      </svg>
                    </OverlayTrigger>
                  </span>
                </h5>

                {!propertyFeatureReorder ? (
                  <a
                    href={void 0}
                    className="intro-right-link pe-2 ps-2 intro-reorder-btn"
                    onClick={() => {
                      setPropertyFeatureReorder(true);
                    }}
                  >
                    Reorder
                  </a>
                ) : (
                  <a
                    href={void 0}
                    className="intro-right-link pe-2 ps-2 intro-reorder-btn"
                    onClick={() => {
                      saveReordedPropertyFeature();
                    }}
                  >
                    {" "}
                    Done
                  </a>
                )}
              </div>
              <div className="nicetohaves-wrap pt-0 pb-0 border-bottom-0">
                <div className="nicetohaves-inner-wrap me-0">
                  {featuredProperty
                    .sort((a, b) => a.order - b.order)
                    .map((feature, index) => {
                      let className = propertyFeatureReorder
                        ? "nicetohaves-tag reorder-tag"
                        : "nicetohaves-tag";
                      return (
                        <div
                          className={className}
                          draggable={propertyFeatureReorder}
                          id={index}
                          onDragOver={(ev) => ev.preventDefault()}
                          onDragStart={handleDrag}
                          onDrop={handleDrop}
                        >
                          {propertyFeatureReorder && (
                            <span className="drag-handle">
                              <img src={DragIcon} alt={DragIcon} />
                            </span>
                          )}

                          <div className="nicetohaves-tag-icon">
                            <img
                              src={
                                feature?.icon
                                  ? require("../../images/" + feature?.icon)
                                  : ""
                              }
                            />
                          </div>
                          <span>{feature?.text}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="">
          <button
            variant=""
            className="btn preview-intro-link-btn"
            onClick={PreviewModalShow}
          >
            Preview introduction
          </button>
          <div className="modal-btn-wrap modal-btnwrap-width">
            <Button
              variant="tertiary"
              className="btn btn-tertiary me-3 btn-margin"
              onClick={props.onClickCustizeIntroClose}
              disabled={propertyFeatureReorder ? true : false}
            >
              Close
            </Button>
            <Button
              variant="primary"
              className="btn"
              onClick={() => {
                SaveChanges();
              }}
              disabled={propertyFeatureReorder ? true : false}
            >
              Save Changes
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <IntroductionPreviewModal
        show={showPreviewModal}
        onHide={PreviewModalClose}
        selectedProperty={selectedProperty}
      />
    </>
  );
};
export default CustomizeIntroModal;
